/**
 * One flow map, loaded and kept saved.
 *
 * The network half of a CRM flow map: the first load, the autosave, and what
 * happens when a save meets somebody else's. The decisions are the pure
 * machine in `saveState.ts`; this hook only times them.
 *
 * - Loading. A reply for a map the screen has already left is dropped (the
 *   `wanted` ref), and a 404 is its own phase, because "that guide is gone" and
 *   "the request failed" send a reader to two different places.
 * - Autosave. AUTOSAVE_DELAY_MS after the last edit. One request at a time; the
 *   edits made while it is out go in the next one, as the newest document only.
 * - Conflict. Autosave stops and the server's copy is exposed until the user
 *   picks `loadLatest` or `keepMine`.
 * - Leaving. Unsaved work is sent when the screen unmounts (a click to another
 *   CRM page), and the browser asks before a tab with unsaved work closes. Work
 *   held back by an unanswered conflict cannot be sent, so it waits on the
 *   shelf in `parkedWork.ts` and comes back when this map opens again.
 *
 * The document and the machine live in a per-map session object as well as in
 * React state. The save loop runs across awaits and has to read the newest
 * values, including after the component is gone, which state alone cannot give it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { emptyFlowDoc, type FlowMapDoc } from "@wa/widgets/flow-map/model";
import { ApiError } from "../../../../lib/api";
import { getFlowMap, saveFlowMap, type FlowMapView, type FlowOwner } from "../../../../lib/flowMap";
import { useUnloadWarning } from "../../../telephony/useUnloadWarning";
import { parkWork, takeParkedWork } from "./parkedWork";
import {
  AUTOSAVE_DELAY_MS,
  canStartSave,
  hasUnsaved,
  initialSaveState,
  reduceSave,
  saveFailureEvent,
  wantsAutosave,
  type SaveState,
} from "./saveState";

/** Where the load stands. "missing" is a 404: the guide or project is gone, or hidden from this user. */
export type FlowMapPhase = "loading" | "ready" | "missing" | "error";

export interface FlowMapDocState {
  phase: FlowMapPhase;
  /** The local document: what the canvas shows, saved or not. */
  doc: FlowMapDoc;
  /** The server's last word on this map (load, save or conflict), read for its metadata. */
  view: FlowMapView | null;
  save: SaveState;
}

export interface FlowMapDocApi extends FlowMapDocState {
  /** A finished gesture on the canvas. Saved AUTOSAVE_DELAY_MS after the last one. */
  edit: (next: FlowMapDoc) => void;
  /**
   * Put a whole document on the map and save it now, without waiting for the
   * autosave: a starter map on an empty canvas, or an older version restored
   * from the history. Either way it is an ordinary save, so the version it
   * replaces goes into the history and a conflict is answered as usual.
   */
  start: (doc: FlowMapDoc) => void;
  /** Send the unsaved work now (after a failed save). */
  retry: () => void;
  /** Load again after a failed load. */
  reload: () => void;
  /** Conflict: take the server's map and drop the local work. */
  loadLatest: () => void;
  /** Conflict: save the local map over the server's. */
  keepMine: () => void;
}

interface Session extends FlowMapDocState {
  owner: FlowOwner;
  ownerId: string;
  /** False once the screen has left this map. A save already under way still finishes; nothing is painted. */
  alive: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  paint: (state: FlowMapDocState) => void;
}

/** What the canvas is handed while a map loads. Never edited: nothing is drawn until the load is back. */
const LOADING_DOC: FlowMapDoc = emptyFlowDoc();

function blank(): FlowMapDocState {
  return { phase: "loading", doc: LOADING_DOC, view: null, save: initialSaveState() };
}

function paint(s: Session): void {
  if (s.alive) s.paint({ phase: s.phase, doc: s.doc, view: s.view, save: s.save });
}

function clearTimer(s: Session): void {
  if (s.timer !== null) {
    clearTimeout(s.timer);
    s.timer = null;
  }
}

/** (Re)start the debounce: the newest edit waits AUTOSAVE_DELAY_MS before it goes out. */
function schedule(s: Session): void {
  clearTimer(s);
  if (!wantsAutosave(s.save)) return;
  s.timer = setTimeout(() => {
    s.timer = null;
    void pump(s);
  }, AUTOSAVE_DELAY_MS);
}

/**
 * Send the newest document, if the machine allows a save now.
 *
 * Called by the debounce, by retry / keep-mine / start, and on the way out. It
 * refuses while a request is out: that request looks again when it ends, which
 * is how the edits made in the meantime are sent without a second request ever
 * running beside the first.
 */
async function pump(s: Session): Promise<void> {
  if (!canStartSave(s.save)) return;
  clearTimer(s);
  const doc = s.doc;
  s.save = reduceSave(s.save, { type: "save-start" });
  paint(s);
  try {
    const view = await saveFlowMap(s.owner, s.ownerId, s.save.baseVersion, doc);
    // Kept for its metadata only. Its document is the one that was sent, and
    // the local one may already be newer.
    s.view = view;
    s.save = reduceSave(s.save, { type: "save-ok", version: view.version });
  } catch (err) {
    s.save = reduceSave(s.save, saveFailureEvent(err));
  }
  paint(s);
  // Edits made while the request was out. A debounce still running sends them
  // itself; one that already fired found this request in flight and gave up, so
  // they go now. After the screen has gone there is no debounce left to wait for.
  if (s.timer === null && wantsAutosave(s.save)) void pump(s);
  // The answer to the last save the screen ever sent was a conflict. Nobody is
  // left to show the banner to, so the work goes on the shelf for the return
  // visit rather than dying with the component.
  parkIfUnanswered(s);
}

/** A conflict the user never answered keeps the work: on the shelf, for this map's next visit. */
function parkIfUnanswered(s: Session): void {
  if (s.alive || !s.save.conflict || !hasUnsaved(s.save)) return;
  parkWork(s.owner, s.ownerId, { doc: s.doc, view: s.view, save: s.save });
}

export function useFlowMapDoc(owner: FlowOwner, ownerId: string): FlowMapDocApi {
  const [state, setState] = useState<FlowMapDocState>(blank);
  const [attempt, setAttempt] = useState(0);
  // The session the screen is showing. Replies and actions for any other are dropped.
  const wanted = useRef<Session | null>(null);

  useEffect(() => {
    const s: Session = { ...blank(), owner, ownerId, alive: true, timer: null, paint: setState };
    wanted.current = s;

    // Work left here by an earlier visit comes back as it was, banner included.
    // No fresh load: the server's copy in that banner is what the user was
    // asked to choose against, and a newer one would move the question under
    // them. If the server has since moved again, "keep mine" meets the next
    // conflict honestly.
    const teardown = () => {
      s.alive = false;
      if (wanted.current === s) wanted.current = null;
      clearTimer(s);
      // The last chance for unsaved work. `pump` refuses a map in conflict
      // (sending it would overwrite somebody else's save without the user
      // having chosen to) and leaves a request already out to finish first.
      if (s.phase === "ready") void pump(s);
      // What `pump` would not send. A request still in flight parks its own
      // answer when it lands, so a conflict that arrives after this runs is
      // kept too.
      parkIfUnanswered(s);
    };

    const left = takeParkedWork(owner, ownerId);
    if (left) {
      s.doc = left.doc;
      s.view = left.view;
      s.save = left.save;
      s.phase = "ready";
      paint(s);
      return teardown;
    }

    paint(s);
    getFlowMap(owner, ownerId).then(
      (view) => {
        if (wanted.current !== s) return;
        s.view = view;
        s.doc = view.doc;
        s.phase = "ready";
        s.save = reduceSave(s.save, { type: "loaded", version: view.version });
        paint(s);
      },
      (err: unknown) => {
        if (wanted.current !== s) return;
        s.phase = err instanceof ApiError && err.status === 404 ? "missing" : "error";
        paint(s);
      },
    );
    return teardown;
  }, [owner, ownerId, attempt]);

  const actions = useMemo(() => {
    /** The session these actions belong to, while the screen still shows it and it has loaded. */
    const live = (): Session | null => {
      const s = wanted.current;
      return s && s.owner === owner && s.ownerId === ownerId && s.phase === "ready" ? s : null;
    };
    return {
      edit(next: FlowMapDoc) {
        const s = live();
        if (!s || next === s.doc) return;
        s.doc = next;
        s.save = reduceSave(s.save, { type: "edit" });
        paint(s);
        schedule(s);
      },
      start(doc: FlowMapDoc) {
        const s = live();
        if (!s) return;
        s.doc = doc;
        s.save = reduceSave(s.save, { type: "edit" });
        paint(s);
        void pump(s);
      },
      retry() {
        const s = live();
        if (s) void pump(s);
      },
      loadLatest() {
        const s = live();
        const theirs = s?.save.conflict;
        if (!s || !theirs) return;
        s.doc = theirs.doc;
        s.view = theirs;
        s.save = reduceSave(s.save, { type: "load-latest" });
        paint(s);
      },
      keepMine() {
        const s = live();
        if (!s?.save.conflict) return;
        s.save = reduceSave(s.save, { type: "keep-mine" });
        paint(s);
        void pump(s);
      },
    };
  }, [owner, ownerId]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useUnloadWarning(hasUnsaved(state.save));

  return { ...state, ...actions, reload };
}
